<template>
  <v-container fluid class='pa-0'>

    <!-- Data Table -->
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredItems"
      item-key="mediaItemId"
      :search="search"
      :custom-filter="customFilter"
      :loading="isLoading"
      show-select
      :single-select="isCoverImagePicker"
      :no-data-text="noDataText"
      :sort-by.sync="sortBy"
      :sort-desc.sync="descending"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{ 
        itemsPerPageOptions: itemsPerPageOptions,
         'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >

      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>

        <!-- Toolbar -->
        <v-toolbar dense dark flat :color="toolbarColor">
          <v-btn fab small @click="cancel" class="mr-3 elevation-0" :color="toolbarColorDark">
            <v-icon>close</v-icon>
          </v-btn>

          <v-toolbar-title :class="titleClass"><span class="title">{{ title }}</span></v-toolbar-title>

          <v-spacer />

          <v-toolbar-items>
            <v-btn
              @click="selectMedia"
              large
              :color="toolbarColorDark"
              class="elevation-0"
              :disabled="!isMediaSelected"
              >
              <span class="subheading font-weight-medium">{{ getExpression('Button', 'OK') }}</span>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">
        
          <!-- Container -->
          <v-col v-if="showContainer" cols="12" sm="3">
            <v-select
              v-model="container"
              :items="containers"
              item-value="containerName"
              item-text="displayName"
              :label="containerLabel"
              :placeholder="containerPlaceholder"
              :disabled="disableContainer"
              class="mr-3"
            />
          </v-col>

          <!-- Media Type -->
          <template v-if="!isCoverImagePicker">
            <v-col cols="12" md="2">
              <v-select
                v-model="mediaType"
                :items="mediaTypes"
                item-value="value"
                item-text="text"
                :label="getExpression('Label', 'ContentType')"
                placeholder="Select type"
                :disabled="disableMediaType"
                class="mr-3"
                >
              </v-select>
            </v-col>
          </template>

          <!-- Search Text -->
          <v-col cols="12" :md="searchColWidth">
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              :disabled="isLoading"
              clearable
              selectable-key="messageId"
            />
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.contentType="{ item }">
        <span class="text-left pl-0">
          <v-icon v-if="getContentTypeAbbr(item.contentType) === 'image'" large>photo</v-icon>
          <v-icon v-if="getContentTypeAbbr(item.contentType) === 'video'" large>movie</v-icon>
          <v-icon v-if="getContentTypeAbbr(item.contentType) === 'audio'" large>audiotrack</v-icon>
        </span>
      </template>
      
      <template v-slot:item.thumbnail="{ item }">
        <template>
          <v-img
            v-if="getMediaType(item.contentType) !== 'audio'" 
            width="50"
            height="50"
            :src="getThumbnailUrl(item)"
            class="text-center"
          ></v-img>
          <v-icon v-else large color="grey darken-2">volume_up</v-icon>
        </template>
      </template>

      <template v-slot:item.kioskFolder="{ item }">
        <span class="text-left">{{ getBehaviourDescription(item.kioskFolder) }}</span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left">
          <app-date :date="item.createDate" :format="'MMM-DD-YYYY'"/>
        </span>
      </template>
    </v-data-table>

  </v-container>
</template>

<script>
import appService from '@/app.service'
import { mapGetters } from 'vuex'
import AppDate from '@/components/App/AppDate'
import { AppColor, AppStyle, Container, KioskFolder, ThumbnailUrl, GenieMediaSourceId, UserRole, Base64Image } from '@/config.js'

export default {
  components: {
    AppDate
  },
  name: 'MediaSelector',
  props: {
    value: {
      type: Object,
      default: null
    },
    title: {
      type: String,
      default: 'Select Media'
    },
    ownerName: {
      type: String,
      default: ''
    },
    defaultContainer: {
      type: String,
      default: null
    },
    containers: {
      type: Array,
      default: null,
    },
    containerLabel: {
      type: String,
      default: 'Library',
    },
    containerPlaceholder: {
      type: String,
      default: 'Select library',
    },
    toolbarColor: {
      type: String,
      default: AppColor.Medium
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Dark
    }
  },
  data() {
    return {
      // colors
      appColor_DeleteButton: AppColor.DeleteButton,
      appColor_Progress: AppColor.Progress,

      // containers
      container_Public: Container.Public,
      container_Manuals: Container.Manuals,
      residentPrefix: Container.ResidentPrefix,

      // kiosk
      kioskPrefix: Container.KioskPrefix,
      kioskFolder_Ambient: KioskFolder.Ambient,
      kioskFolder_Announcement: KioskFolder.Announcement,
      kioskFolder_Email: KioskFolder.Email,

      // uri
      thumbnailUrl_Genie: ThumbnailUrl.Genie,

      // icons
      base64Image_AudioIcon: Base64Image.AudioIcon,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      // media sources
      sourceId_Resident: GenieMediaSourceId.Resident,
      sourceId_Kiosk: GenieMediaSourceId.Kiosk,

      // roles
      role_Manager: UserRole.Manager,

      container: null,

      sortBy: 'createDate',
      descending: true,
      selected: [],

      containerMediaItems: [],
      mediaType: 'all',

      isLoading: true,
      search: '',
      itemsPerPageOptions: [10, 15, 25, 100]
    }
  },
  computed: {
    ...mapGetters(['profile',
      'activeKioskItem',
      'activeResidentKioskItem',
      'activeResidentItem',
      'isAdmin',
      'isKioskManager',
      'isResidentManager',
      'currentRoute',
      'previousRoute',
      'locale'
    ]),
    noDataText () {
      const self = this

      if (self.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (self.containers.length === 0) {
        return this.getExpression('Alert', 'NoMediaContainers')
      }
      return self.mediaItems.length === 0
        ? this.getExpression('Alert', 'NoMedia')
        : ''
    },
    mediaItems () {
      const self = this
      const mediaContainers = self.containerMediaItems
        .filter(m => m.container === self.container)

      return mediaContainers.length > 0
        ? mediaContainers[0].mediaItems
        : []
    },
    filteredItems () {
      const self = this
      const mediaType = self.mediaType

      if (mediaType !== 'all') {
        return self.mediaItems.filter(i => {
          return (
            self.getMediaType(i.contentType) === mediaType ||
            mediaType === 'all'
          )
        })
      } else {
        return self.mediaItems
      }
    },
    params () {
      return this.value
    },
    controlId () {
      return this.params !== null 
        ? this.params.controlId
        : null
    },
    playlistId () {
      return this.params !== null 
        ? this.isPlaylistPicker
          ? this.params.controlId
          : null
        : null
    },
    isPlaylistPicker () {
      return this.params !== null 
        ? this.params.selectionType === 'kiosk-playlist' ||
        this.params.selectionType === 'resident-playlist'
        : false
    },
    coverMediaItemId () {
      return this.params !== null 
        ? this.params.selectionType === 'cover-image'
          ? this.params.controlId
          : null
        : null
    },
    isCoverImagePicker () {
      return this.params !== null 
        ? this.params.selectionType === 'cover-image'
        : false
    },
    headers () {
      const self = this
      var headers = []

      headers.push({
        value: 'mediaItemId',
        align: ' d-none'
      })

      headers.push({
        value: 'thumbnail',
        align: 'left',
        width: '3%',
        sortable: false,
        class: 'pr-0'
      })

      headers.push({
        value: 'contentType',
        align: 'left',
        width: '3%',
        class: 'pr-0'
      })

      headers.push({
        text: self.getExpression('Label', 'Title'),
        value: 'title',
        align: 'left',
        width: '30%',
        class: 'pr-0'
      })

      headers.push({
        text: self.getExpression('Label', 'Description'),
        value: 'description',
        align: 'left',
        width: self.isKiosk
            ? '40%'
            : '50%',
        class: 'pr-0'
      })

      if (self.isKiosk) {
        headers.push({
          text: self.getExpression('Label', 'Behaviour'),
          value: 'kioskFolder',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        })
      }

      return headers
    },
    mediaTypes () {
      return [
        {
          value: 'all',
          text: this.getExpression('MenuItem', 'All')
        },
        {
          value: 'audio',
          text: this.getExpression('MenuItem', 'Audio')
        },
        {
          value: 'image',
          text: this.getExpression('MenuItem', 'Image')
        },
        {
          value: 'video',
          text: this.getExpression('MenuItem', 'Video')
        }
      ]
    },   
    isKiosk () {
      return this.params.selectionType === 'kiosk-playlist' ||
      this.params.selectionType === 'kiosk-email'
    },
    isMediaSelected () {
      return this.selected.length > 0
    },
    disableMediaType () {
      return this.containers.length === 0 || this.isLoading
    },
    disableContainer () {
      return this.containers.length <= 1 || this.isLoading
    },
    showContainer () {
      if (this.isCoverImagePicker) return false
      return (this.isPlaylistPicker && this.containers.length > 1) || this.isAdmin
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    },
    searchColWidth () {
      return this.isCoverImagePicker
        ? '12'
        : this.showContainer
          ? '7'
          : '10'
    }
  },
  methods: {
    loadMediaItems () {
      const self = this

      var mediaContainers = self.containerMediaItems.filter(m => m.container === self.container)

      if (mediaContainers.length === 0) {
        const newItem = {
          container: self.container,
          mediaItems: []
        }
        mediaContainers.push(newItem)
        self.containerMediaItems.push(newItem)

        const mediaType = self.isCoverImagePicker ? 'image' : null
        self.isLoading = true

        appService.getMediaItems(self.container, self.playlistId, self.isKiosk, mediaType).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            mediaContainers[0].mediaItems = data.mediaItems
            if (self.isCoverImagePicker) self.preSelectItem() 
          }
          self.isLoading = false

        }).catch(e => { self.handleCriticalError(e) })   
      } else if (self.isCoverImagePicker) {
        self.preSelectItem() 
      }
    },

    preSelectItem () {
      const self = this

      self.selected = []
      self.mediaType = 'image'

      // if a selected blob was passed in then pre-select it
      const items = self.mediaItems.filter(m => { return m.mediaItemId === self.coverMediaItemId })
      if (items.length > 0) {
        const item = items[0]
        self.selected.push(item)
      }
    },

    selectMedia () {
      const selected = this.selected
      this.$emit('ok', { selectedMedia: selected })

      if (!this.isCoverImagePicker) {
        this.selected = []
      }
    },

    cancel () {
      if (!this.isCoverImagePicker) {
        this.selected = []
        this.mediaType = 'all'
      }
      
      this.$emit('cancel')
    },

    getThumbnailUrl (item) {
      const mediaType = this.getMediaType(item.contentType)
      if (mediaType !== 'audio') {
        const blobName = item.blobName
        const ext = blobName.split('.').pop()
        const thumnailName = blobName.replace(ext, 'jpg')

        const url = `${this.thumbnailUrl_Genie}/${item.containerName}/${thumnailName}`.replace('#', '%23')
        item.thumbnail = url

        return url
      } else {
        const url = this.base64Image_AudioIcon
        item.thumbnail = url

        return url
      }
    },

    getBehaviourDescription (kioskFolder) {
      switch (kioskFolder) {
        case this.kioskFolder_Ambient:
          return this.getExpression('Label', 'Standard')
        case this.kioskFolder_Announcement:
          return this.getExpression('Label', 'Announcement')
        case this.kioskFolder_Email:
          return "Email"
        default:
          return null
      }
    },
    
    customFilter (value, search, item) {
      const description = item.description ?? ''
      const addedBy = item.addedBy ?? ''
      const behaviour = this.getBehaviourDescription(item.kioskFolder) ?? ''

      return (
        (value != null &&
          search != null &&
          item.title.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1) ||
          description.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          addedBy.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          behaviour.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
      )
    },

    handleError (result) {
      this.$error.show({
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  watch: {
    container (newValue, oldValue) {
      if (oldValue !== null) {
        this.loadMediaItems()
      }
    },

    controlId (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.container = this.defaultContainer
        this.containerMediaItems = [ { container: null } ]
        this.loadMediaItems()
      }
    }
  }
}
</script>
